import './AddInputModal.scss';
import { useLayoutEffect, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { isAfter, isBefore, startOfToday } from 'date-fns';
import { Button } from '@Components/Button';
import { Radio } from '@Components/FormCheck';
import { Appearance, Design } from '@Components/designConstants';
import { DateInputField } from '@Components/DatePicker/DateInputField/DateInputField';

interface AddOutcomeDataModalProps {
  show: boolean;
  handleClose: () => void;
  createTable: (
    kpiName: string,
    kpiDescription: string,
    unitType: 'Dollars' | 'Count',
    startDate: Date,
    endDate?: Date
  ) => void;
}

export const AddOutcomeDataModal = ({
  show,
  handleClose,
  createTable
}: AddOutcomeDataModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [kpiName, setKpiTitle] = useState('');
  const [kpiDescription, setKpiDescription] = useState('');
  const [unitType, setUnitType] = useState<'Dollars' | 'Count' | ''>('');
  const [startDateStr, setStartDateStr] = useState('');
  const [startDateError, setStartDateError] = useState(false);
  const [endDateStr, setEndDateStr] = useState('');
  const [endDateError, setEndDateError] = useState(false);

  // Resize textarea for kpiDescription when content is updated
  useLayoutEffect(() => {
    const textarea = document.getElementById('kpiDescriptionTextarea');

    if (textarea) {
      textarea.style.height = `${Math.max(textarea.scrollHeight, 64)}px`;
    }
  }, [kpiDescription]);

  const resetState = () => {
    setIsSubmitting(false);
    setKpiTitle('');
    setKpiDescription('');
    setUnitType('');
    setStartDateStr('');
    setStartDateError(false);
    setEndDateStr('');
    setEndDateError(false);
  };

  const submitDisabled =
    isSubmitting ||
    !kpiName ||
    !kpiDescription ||
    kpiDescription.length > 150 ||
    !unitType ||
    !startDateStr ||
    startDateError ||
    endDateError;

  return (
    <Modal
      className='add-input-modal add-outcome-data-modal'
      show={show}
      onHide={handleClose}
      size='xl'
      centered
    >
      <Modal.Header>
        <h4 className='modal-title'>Add Outcome Data</h4>
        <div className='sub-title'>*Required fields marked with an asterisk.</div>
      </Modal.Header>
      <Modal.Body>
        <div className='kpi-name-input'>
          <div className='section-title'>
            KPI Name<span className='asterisk'>*</span>
          </div>
          <Form.Control
            placeholder='Ex: Web Order'
            value={kpiName}
            onChange={e => setKpiTitle(e.target.value)}
          />
          <div className='input-explanation'>
            <p>A measurable target that indicates business performance.</p>
          </div>
        </div>
        <div className='kpi-description-input'>
          <div className='section-title'>
            KPI Description<span className='asterisk'>*</span>
          </div>
          <Form.Control
            as='textarea'
            id='kpiDescriptionTextarea'
            placeholder='Ex: A customer places an order on our website.'
            value={kpiDescription}
            onChange={e => setKpiDescription(e.target.value)}
            isInvalid={kpiDescription.length > 150}
          />
          <Form.Control.Feedback type='invalid'>Max 150 characters</Form.Control.Feedback>
          <div className='input-explanation'>
            <p>A description of how the KPI event happens.</p>
          </div>
        </div>
        <div className='unit-type-input'>
          <div className='section-title'>
            Type of Unit<span className='asterisk'>*</span>
          </div>
          <Radio
            label='Dollars'
            id='dollars'
            checked={unitType === 'Dollars'}
            onChange={() => setUnitType('Dollars')}
          />
          <Radio
            label='Count/Volume'
            id='count'
            checked={unitType === 'Count'}
            onChange={() => setUnitType('Count')}
          />
        </div>
        <div className='time-frame-input'>
          <div className='section-title'>Time Frame</div>
          <DateInputField
            label={
              <>
                Start Date<span className='asterisk'>*</span>
              </>
            }
            value={startDateStr}
            setValue={setStartDateStr}
            customValidation={[
              {
                validate: value => {
                  const parsedDate = new Date(value);

                  const isValid = isBefore(parsedDate, startOfToday());
                  setStartDateError(!isValid);
                  return isValid;
                },
                errorMessage: 'Start date must be in the past.'
              }
            ]}
            infoMessage='mm/dd/yyyy'
          />
          <DateInputField
            label='End Date (optional)'
            className='optional'
            value={endDateStr}
            setValue={setEndDateStr}
            customValidation={[
              {
                validate: value => {
                  const parsedDate = new Date(value);

                  const isValid = isBefore(parsedDate, startOfToday());
                  setEndDateError(!isValid);
                  return isValid;
                },
                errorMessage: 'End date must be in the past.'
              },
              {
                validate: value => {
                  const parsedDate = new Date(value);
                  const parsedStartDate = new Date(startDateStr);

                  const isValid = isAfter(parsedDate, parsedStartDate);
                  setEndDateError(!isValid);
                  return isValid;
                },
                errorMessage: 'End date must be after start date.'
              }
            ]}
            infoMessage='mm/dd/yyyy'
          />
          <div className='input-explanation date-input-explanation'>
            <p>
              Important: Data should be entered daily whenever possible. If your data has been
              aggregated, please disaggregate it to the daily level.
            </p>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          onClick={() => {
            handleClose();
            resetState();
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={async () => {
            if (!unitType) return;
            setIsSubmitting(true);
            await createTable(
              kpiName,
              kpiDescription,
              unitType,
              new Date(startDateStr),
              endDateStr ? new Date(endDateStr) : undefined
            );
            handleClose();
            resetState();
          }}
          disabled={submitDisabled}
          loading={isSubmitting}
        >
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
